import { addDays } from '../packs/deadlines.js';
import type { EventKind, IDeadlineRule, IProgram } from './types.js';

/*
 * Sources checked on 2026-10-06 (all still "pending review" by a lawyer):
 * - IRPA s.72(2)(b), leave to the Federal Court: 15 days (matter arising in Canada) or 60 days (outside).
 * - IRPA s.63(3) and (4), and the Immigration Appeal Division Rules, 2022 (SOR/2022-277, in force
 *   2023-01-14): removal order appeals within 30 days after receiving the removal order; residency
 *   obligation appeals (decision made outside Canada) within 60 days after receiving the decision and
 *   reasons. https://laws-lois.justice.gc.ca/eng/regulations/SOR-2022-277/FullText.html
 * - IRPA s.64: no IAD appeal for some inadmissibility (security, human rights, serious criminality,
 *   organized criminality); s.64(3), misrepresentation, unless the person is the sponsor's spouse,
 *   partner or child. https://laws-lois.justice.gc.ca/eng/acts/I-2.5/section-64.html
 * - Immigration and Refugee Protection Regulations s.159.91: RAD notice within 15 days, appeal
 *   perfected within 30 days of receiving the RPD's written reasons. The IRB's 2020 practice notice
 *   extends perfecting to 45 days "until further notice", and the IRB's appellant's record page (updated
 *   2025-10-30, checked 2026-10-06) still says 45, so 45 is the default and 30 a choice.
 *   https://irb-cisr.gc.ca/en/filing-refugee-appeal/filing-a-refugee-appeal/Pages/preparing-submitting-appellant-record.aspx
 *   https://www.irb-cisr.gc.ca/en/legal-policy/procedures/Pages/practice-notice-extending-time-perfecting-appeal.aspx
 * - Federal Courts Citizenship, Immigration and Refugee Protection Rules (SOR/93-22), rules 9 and 10:
 *   applicant's record within 30 days after filing the application for leave, or, when it said the
 *   reasons hadn't been received, 30 days after receiving them (or the notice that there are none).
 *   https://laws-lois.justice.gc.ca/eng/regulations/SOR-93-22/section-10.html
 * - IRCC: maintained status (apply before the permit expires; online, before midnight UTC that day),
 *   restoration within 90 days of losing status, PGWP within 180 days of confirmation of completion.
 * Service of the application for leave is not calculated: its period isn't confirmed yet.
 */

/** Shown under every calculated date: we never move a date that falls on a weekend or holiday. */
export const WEEKEND_NOTE: string = 'If this falls on a weekend or holiday, the real deadline may be later. Don\'t rely on that.';

/**
 * Deadlines that follow common IRCC, IRB and Federal Court events. Calendar days. Every one is shown
 * as calculated, for a person to check against the letter and confirm; a date the letter states
 * always wins.
 */
export const COMMON_DEADLINES: IDeadlineRule[] = [
  { id: 'biometrics', event: 'biometricsLetter', label: 'Give biometrics', days: 30, letterFirst: true, citation: 'IRCC biometric instruction letter (usually 30 days)', caveat: 'The letter states the date; use it.', review: 'pending' },
  { id: 'medical', event: 'medicalInstructions', label: 'Medical exam done', days: 30, letterFirst: true, citation: 'IRCC medical exam instructions (IMM 1017 / IMM 1020), 30 days', caveat: 'The instructions state the date; use it.', review: 'pending' },
  { id: 'documents', event: 'documentRequest', label: 'Send the requested documents', days: 30, letterFirst: true, citation: 'IRCC requests for documents (usually 30 days)', caveat: 'The request states its own deadline; use it.', review: 'pending' },
  { id: 'fairness', event: 'fairnessLetter', label: 'Answer the procedural fairness letter', asStated: { min: 7, max: 30 }, citation: 'The letter sets the date (usually 7 to 30 days)', caveat: 'Enter the date the letter gives. Ask for more time in writing before it passes.', review: 'pending' },
  {
    id: 'leaveInCanada', event: 'refusalInCanada', label: 'Apply for leave and judicial review (Federal Court)', days: 15,
    citation: 'IRPA s.72(2)(b): 15 days for a matter arising in Canada',
    caveat: 'The period runs from when the person was told of, or learned of, the decision.',
    alt: { tick: 'This matter arose outside Canada', days: 60, citation: 'IRPA s.72(2)(b): 60 days for a matter arising outside Canada', caveat: 'Where the matter arose decides it, not where the office that decided it is.' },
    review: 'pending'
  },
  {
    id: 'iadSponsorship', event: 'sponsorshipRefusal', label: 'File the notice of appeal (Immigration Appeal Division)', days: 30,
    citation: 'IRPA s.63(1) and the IAD Rules, 2022: 30 days after receiving the refusal',
    caveat: 'Some refusals can\'t be appealed to the IAD (IRPA s.64: serious criminality, some misrepresentation). Check before relying on this date.',
    review: 'pending'
  },
  {
    id: 'fcRecord', event: 'leaveFiled', label: 'File the applicant\'s record (Federal Court)', days: 30,
    citation: 'Federal Courts Citizenship, Immigration and Refugee Protection Rules, r.10(1)(a): 30 days after filing the application for leave',
    caveat: 'If the application said the reasons hadn\'t been received, the record is due 30 days after they arrive instead (r.10(1)(b)); record "Reasons received after the Federal Court asked for them" when they do.',
    review: 'pending'
  },
  {
    id: 'fcRecordAfterReasons', event: 'fcReasons', label: 'File the applicant\'s record (Federal Court)', days: 30,
    citation: 'Federal Courts Citizenship, Immigration and Refugee Protection Rules, r.10(1)(b) and r.9: 30 days after receiving the reasons, or the notice that there are none',
    review: 'pending'
  },
  {
    id: 'keepStatus', event: 'permitExpiry', label: 'Apply before this date to keep status', days: 0,
    citation: 'IRCC: an application to extend made before the permit expires keeps the person\'s status while IRCC decides (maintained status)',
    caveat: 'Online, it must be sent before midnight UTC on the expiry day (8 p.m. in Toronto in summer, 7 p.m. in winter). Only while the person is in Canada with status; a new IEC participation doesn\'t keep status.',
    review: 'pending'
  },
  {
    id: 'keepStatusSoon', event: 'permitExpiry', label: 'Permit expires in 30 days: apply to extend now', days: -30,
    citation: 'IRCC: apply at least 30 days before a study permit or visitor status expires',
    caveat: 'A warning 30 days before the expiry date.',
    review: 'pending'
  },
  {
    id: 'restore', event: 'statusLost', label: 'Apply to restore status', days: 90,
    citation: 'IRCC: an application to restore status must be received no more than 90 days after status expired',
    caveat: 'Counted from the day status expired. Some workers with a province\'s letter of support aren\'t held to the 90 days.',
    review: 'pending'
  }
];

/**
 * Residency obligation appeals to the IAD: two events with different periods. The 30-day one comes
 * first and is the default wherever staff choose between them.
 */
export const RESIDENCY_DEADLINES: IDeadlineRule[] = [
  { id: 'removalOrderAppeal', event: 'residencyRemovalOrder', label: 'File the notice of appeal (Immigration Appeal Division)', days: 30, citation: 'IRPA s.63(3) and the IAD Rules, 2022: 30 days after receiving the removal order', caveat: 'For a removal order made in Canada, at a port of entry or inland.', review: 'pending' },
  { id: 'residencyAppeal', event: 'residencyRefusal', label: 'File the notice of appeal (Immigration Appeal Division)', days: 60, citation: 'IRPA s.63(4) and the IAD Rules, 2022: 60 days after receiving the officer\'s decision and reasons', caveat: 'Only for a residency decision made outside Canada by an officer abroad.', review: 'pending' }
];

/** The rules that follow an event, for this program (its own rules first, then the common ones). */
export function rulesFor(program: IProgram, event: EventKind): IDeadlineRule[] {
  const own: IDeadlineRule[] = program.deadlines.filter(r => r.event === event);
  const ids: { [id: string]: boolean } = {};
  for (const r of own) {
    ids[r.id] = true;
  }
  return [...own, ...COMMON_DEADLINES.filter(r => r.event === event && !ids[r.id])];
}

/**
 * The calculated date (YYYY-MM-DD), or undefined when the letter sets it and a person must enter it.
 * `ticked`: staff chose the rule's other period deliberately.
 */
export function dueDate(rule: IDeadlineRule, eventDate: string, ticked: boolean = false): string | undefined {
  const days: number | undefined = ticked && rule.alt ? rule.alt.days : rule.days;
  return days === undefined ? undefined : addDays(eventDate, days);
}
